// 页面尺寸454*320，适配移动设备
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { Button, Col, ConfigProvider, Input, Row } from 'antd'
import { formatMessageNew } from '@/utils/common'
import { destroyModal, modalErrorRefreshCode } from '@/utils/modalUtils'
import '@/pages/AndroidDevice/PhoneLayout.less'
import {
  getRoundScanResult,
  getScanInfoByCarton,
  getSkuInfoByEpc,
  getSkuInfoByQRCode,
  getSkuInfoByUpc,
  manualCheckInfoByCarton,
  manualRoundScanByCarton,
  manualScanByPiece,
  manualScanCount,
  readRfid,
  startRoundScan,
  stopRoundScan,
  validQRCodeAndUpc,
  validRelabeledEpc
} from '../../../services/qrCode/binding'
import { modalConfirm, modalError, modalSuccess } from '../../../utils/modalUtils'
import { PrinterOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons'
import { Dialog as DialogMobile } from 'antd-mobile'
import ReprintPage from '../reprint/print'
import { resetRfid } from '../../../services/qrCode/demo'
import { Base64 } from '../../../utils/base64'

let errorModal = null
let errorUpcModal = null
let errorQrCodeModal = null
let successModal = null
let errorCartonModal = null
let errorNum = 0
const RFID_SCAN_CONTEXT_KEY = 'rfidScanContext'
const UNKNOWN_SKU_CODE = 'UNKNOWN_SKU'
const DEFAULT_ROUND_SCAN_POWER = 1400
const DEFAULT_NORMAL_SCAN_POWER = 500

const getStoredRfidScanContext = () => {
  try {
    const storedContext = localStorage.getItem(RFID_SCAN_CONTEXT_KEY)
    return storedContext ? JSON.parse(Base64.decode(storedContext)) : {}
  } catch (error) {
    try {
      localStorage.removeItem(RFID_SCAN_CONTEXT_KEY)
    } catch (_) {}
    return {}
  }
}

const getRfidScanContextValue = (primaryContext, fallbackContext, key) => {
  const primaryValue = primaryContext?.[key]
  if (primaryValue !== undefined && primaryValue !== null && primaryValue !== '') return primaryValue
  const fallbackValue = fallbackContext?.[key]
  return fallbackValue === undefined || fallbackValue === null ? '' : fallbackValue
}

const normalizeDeviceCode = value => {
  if (value === undefined || value === null) return ''
  const rawValue = String(value).trim()
  if (!rawValue || rawValue === 'null' || rawValue === 'undefined') return ''
  try {
    const parsedValue = JSON.parse(rawValue)
    return parsedValue === undefined || parsedValue === null ? '' : String(parsedValue).trim()
  } catch (_) {
    return rawValue
  }
}

const normalizePowerValue = (value, fallbackValue = '') => {
  if (value === undefined || value === null || value === '') return fallbackValue
  const powerValue = Number(String(value).trim())
  return Number.isFinite(powerValue) ? powerValue : fallbackValue
}

const getStoredDeviceCode = () => {
  try {
    const storedDeviceCode = localStorage.getItem('deviceCode')
    if (!storedDeviceCode) return ''
    return normalizeDeviceCode(JSON.parse(Base64.decode(storedDeviceCode)))
  } catch (_) {
    return ''
  }
}

// 全局时间戳：记录每次进入手动扫码页面的时间（毫秒）。
// 每次触发 `toManualScanPage` 时会刷新此值，方便其它模块或调试使用。
if (typeof window !== 'undefined') {
  window.relabelReworkTime = window.relabelReworkTime || null
}
// 页面主组件，负责整个RFID扫码业务流程
const RfIdScanningPage = () => {
  // 路由跳转和参数获取
  const history = useHistory()
  const location = useLocation()
  const [reprintKey, setReprintKey] = useState(null)
  // 页面参数：标签码、业务类型、工厂
  const storedRfidScanContext = useMemo(() => getStoredRfidScanContext(), [])
  const rfidScanContext = location.state || {}
  const labelCode = getRfidScanContextValue(rfidScanContext, storedRfidScanContext, 'labelCode')
  const businessType = getRfidScanContextValue(rfidScanContext, storedRfidScanContext, 'businessType')
  const plant = getRfidScanContextValue(rfidScanContext, storedRfidScanContext, 'plant')
  const roundPower = normalizePowerValue(
    getRfidScanContextValue(rfidScanContext, storedRfidScanContext, 'roundPower'),
    DEFAULT_ROUND_SCAN_POWER
  )
  const normalPower = normalizePowerValue(
    getRfidScanContextValue(rfidScanContext, storedRfidScanContext, 'normalPower'),
    DEFAULT_NORMAL_SCAN_POWER
  )
  // 输入框引用，便于自动聚焦和操作
  const cartonInputRef = useRef(null) // 箱号输入框
  const upcInputRef = useRef(null) // upc输入框
  const epcInputRef = useRef(null) // epc输入框
  const qrCodeInputRef = useRef(null) // 二维码输入框
  // 从redux获取设备信息
  const macState = useSelector(state => state.commonStore.mac)
  const deviceCode = useSelector(state => state.commonStore.deviceCode)
  // RF scan上报用device code，Redux缺值时从登录缓存补一次。
  const resolvedDeviceCode = useMemo(() => normalizeDeviceCode(deviceCode) || getStoredDeviceCode(), [deviceCode])
  // 页面核心状态，管理所有输入、弹窗、结果等
  const [upcValue, setUpcValue] = useState('') // upc输入框内容
  const [epcValue, setEpcValue] = useState('') // epc输入框内容
  const [skuValueByEpc, setSkuValueByEpc] = useState('') // epc对应sku
  const [skuValueByUpc, setSkuValueByUpc] = useState('') // upc对应sku
  const [skuValueByQr, setSkuValueByQr] = useState('') // qrCode对应sku
  const [scanUpcQrModalVisible, setScanUpcQrModalVisible] = useState(false) // 手动扫码弹窗
  const [physicalSkuCorrectVisible, setPhysicalSkuCorrectVisible] = useState(false) // 物理正确弹窗
  const [physicalSkuInCorrectVisible, setPhysicalSkuInCorrectVisible] = useState(false) // 物理错误弹窗
  const [epcBoundRelabelVisible, setEpcBoundRelabelVisible] = useState(false) // EPC已绑定补标提示弹窗
  const [epcBoundSubmitVisible, setEpcBoundSubmitVisible] = useState(false) // EPC已绑定提交确认弹窗
  const [epcBoundReturnSource, setEpcBoundReturnSource] = useState('') // EPC已绑定弹窗取消后返回来源
  const [epcBoundRollbackEpcList, setEpcBoundRollbackEpcList] = useState([]) // 本次EPC已绑定弹窗取消时需要回滚的EPC
  const [physicalCheckRollbackEpcList, setPhysicalCheckRollbackEpcList] = useState([]) // 实物SKU校验弹窗取消时需要回滚的EPC
  const [epcBoundOriginProductList, setEpcBoundOriginProductList] = useState([]) // EPC已绑定弹窗覆盖前的扫描记录，用于取消时还原
  const [relabelVisible, setRelabelVisible] = useState(false) // 重新贴标弹窗
  const [qrCodeValue, setQrCodeValue] = useState('') // 二维码输入框内容
  const [cartonNoValue, setCartonNoValue] = useState('') // 箱号输入框内容
  const [scanResultList, setScanResultList] = useState([]) // 普通扫描结果列表
  const [scanCompareResult, setScanCompareResult] = useState('') // 校验结果类型
  const [roundScanResultList, setRoundScanResultList] = useState([]) // 环扫结果列表
  const [totalCount, setTotalCount] = useState(0) // 总数
  const [scanCount, setScanCount] = useState(0) // 已扫描数
  const [dialogConfirmVisible, setDialogConfirmVisible] = useState(true) // 手动扫码弹窗确认按钮状态
  const [qrCodeSkuFetched, setQrCodeSkuFetched] = useState(false) // 二维码是否已查到sku
  const [manualStatus, setManualStatus] = useState(false) // 手动校验状态
  const [manualCountStatus, setManualCountStatus] = useState(false) // 手动校验状态
  const [manualScanStatus, setManualScanStatus] = useState(false) // 手动扫码状态
  const [scanProductList, setScanProductList] = useState([]) // 扫描产品列表
  const [validEpcList, setValidEpcList] = useState([]) // 无效epc列表
  const [inputReadOnly, setInputReadOnly] = useState(false) // 输入框只读状态
  const [manualScanVisibleFlag, setManualScanVisibleFlag] = useState(false) // 手动扫码弹窗可见标志
  const [cartonEnterFlag, setCartonEnterFlag] = useState(false) // 箱号已录入标志
  const [cartonDisableFlag, setCartonDisableFlag] = useState(false) // 箱号已录入标志
  const [scanResultShowFlag, setScanResultShowFlag] = useState(false) // 普通扫描结果展示标志
  const [scanRoundResultShowFlag, setScanRoundResultShowFlag] = useState(false) // 环扫结果展示标志
  // 页面索引，控制显示哪一个页面（0-第一页，1-第二页，2-第三页，3-第四页）
  const [pageIndex, setPageIndex] = useState(0)
  const pageIndexRef = useRef(pageIndex)
  const epcValueRef = useRef(epcValue)
  const scanRoundFlag = useRef(scanRoundResultShowFlag)
  const suppressEnterModalCloseUntilRef = useRef(0)
  // 手动扫码时间戳（ref），用于保证同一次 toManualScanPage 操作中的多个请求参数一致
  const manualScanTimestampRef = useRef(null)
  // 页面标题key
  const titleKey = 'languageObj.qrCode.' + labelCode
  // 按钮状态相关
  const [physicalSkuCorrectBtnActive, setPhysicalSkuCorrectBtnActive] = useState(false) // 物理正确按钮激活状态
  const [physicalSkuInCorrectBtnActive, setPhysicalSkuInCorrectBtnActive] = useState(false) // 物理正确按钮激活状态
  const [noRfidTagActive, setNoRfidTagActive] = useState(false) // 无RFID标签按钮激活状态
  const [scanNoResponseActive, setScanNoResponseActive] = useState(false) // 扫描无响应按钮激活状态
  const [relabelValue, setRelabelValue] = useState(null) // 重新贴标输入框内容
  const [showReprint, setShowReprint] = useState(false) // 显示重打标签页面
  const [manualScanResultInvalidVisible, setManualScanResultInvalidVisible] = useState(false) // 手动校验结果无效弹窗
  const [reScanFlag, setReScanFlag] = useState(true) // 重新扫描标志
  const [invalidList, setInvalidList] = useState([]) // 手动校验无效列表
  const renderSkuAndSize = skuAndSize =>
    skuAndSize === UNKNOWN_SKU_CODE ? formatMessageNew('languageObj.qrCode.unknownSku') : skuAndSize
  // 校验结果配置，决定展示的样式和文案
  const bingResultConfig = {
    // 1: 正确，2: EPC多，3: EPC少，4: UPC不匹配，5: 异常，6: 人工确认，7: 新箱号
    1: {
      style: { background: '#6ca744', color: '#fff' },
      key: 'languageObj.qrCode.accurate'
    },
    2: {
      style: { background: '#e74c3c', color: '#fff' },
      key: 'languageObj.qrCode.epcOverage'
    },
    3: {
      style: { background: '#e74c3c', color: '#fff' },
      key: 'languageObj.qrCode.epcShortage'
    },
    4: {
      style: { background: '#e74c3c', color: '#fff' },
      key: 'languageObj.qrCode.upcMismatched'
    },
    5: {
      style: { background: '#f1c40f', color: '#fff' },
      key: 'languageObj.qrCode.Unexpected'
    },
    6: {
      style: { background: '#6ca744', color: '#f1c40f' },
      key: 'languageObj.qrCode.manualConfirm'
    },
    7: {
      style: { background: '#6ca744', color: '#fff' },
      key: 'languageObj.qrCode.newCarton'
    }
  }
  // 当前激活按钮索引（人工校验按钮）
  const [activeIndex, setActiveIndex] = useState(null)
  // 人工校验按钮和消息key
  const btns = useMemo(
    () => [
      'languageObj.qrCode.productShortage',
      'languageObj.qrCode.productOverage',
      'languageObj.qrCode.specialProduct',
      'languageObj.qrCode.manualUnitScan'
    ],
    []
  )
  const msgKeys = useMemo(
    () => [
      'languageObj.qrCode.productShortageMsg',
      'languageObj.qrCode.productOverageMsg',
      'languageObj.qrCode.specialProductMsg',
      'languageObj.qrCode.errorcode_11'
    ],
    []
  )

  // 请求头参数，接口调用时使用
  const queryHeader = {
    isRF: true,
    mac: macState,
    deviceCode: resolvedDeviceCode,
    operator: resolvedDeviceCode
  }

  const startRoundScanWithPower = useCallback(
    (power = roundPower) => {
      if (!startRoundScan) return Promise.resolve()
      return startRoundScan({
        ...queryHeader,
        roundPower: normalizePowerValue(power, roundPower)
      })
    },
    [queryHeader, roundPower]
  )

  const stopRoundScanWithPower = useCallback(
    (power = normalPower) => {
      if (!stopRoundScan) return Promise.resolve()
      return stopRoundScan({
        ...queryHeader,
        normalPower: normalizePowerValue(power, normalPower)
      })
    },
    [normalPower, queryHeader]
  )

  const validateScanContext = useCallback(() => {
    const hasPlant = plant && String(plant).trim()
    const hasBusinessType = businessType !== '' && businessType !== null && businessType !== undefined
    const hasDeviceCode = resolvedDeviceCode && String(resolvedDeviceCode).trim()
    if (hasPlant && hasBusinessType && hasDeviceCode) return true
    if (errorNum !== 0) return false
    errorNum = 1
    errorModal = modalError(
      {
        content: hasDeviceCode
          ? 'RFID scan context is missing. Please go back to menu and enter again.'
          : 'RF Device Code is missing. Please login RF device again.'
      },
      () => {
        errorModal = null
        errorNum = 0
        history.replace('/rf/login')
      }
    )
    return false
  }, [businessType, history, plant, resolvedDeviceCode])

  /**
   * 临时聚焦工具：
   * - 支持 AntD Input 实例（有 .input）和原生 input
   * - 优先调用组件的 focus({ cursor: 'all' })，失败后回退到 DOM focus/select
   * - 聚焦前短暂设置 DOM readOnly = true 以尽量阻止移动端键盘弹出，delay 后恢复
   * - 返回 cleanup 用于清理定时器（如果在 useEffect 中使用）
   */
  const tempFocus = (ref, options = { cursor: 'all' }, restoreDelay = 200, initialDelay = 0) => {
    if (!ref || !ref.current) return () => {}
    let t1 = null
    let t2 = null
    try {
      const comp = ref.current
      const dom = comp && (comp.input || comp) // AntD Input 实例的真实 DOM 常在 .input

      t1 = window.setTimeout(() => {
        try {
          // 先设置只读以抑制移动端键盘
          if (dom) dom.readOnly = true

          // 尝试使用组件 focus
          if (comp && typeof comp.focus === 'function') {
            try {
              comp.focus(options)
            } catch (e) {
              // fallback to DOM
              dom && dom.focus && dom.focus()
              if (dom && dom.select) dom.select()
              else if (dom && dom.setSelectionRange) dom.setSelectionRange(0, (dom.value || '').length)
            }
          } else if (dom && typeof dom.focus === 'function') {
            dom.focus()
            if (dom.select) dom.select()
            else if (dom.setSelectionRange) dom.setSelectionRange(0, (dom.value || '').length)
          }
        } catch (err) {
          // ignore
        }

        // 延时恢复只读
        t2 = window.setTimeout(() => {
          try {
            const cur = ref.current
            const d2 = cur && (cur.input || cur)
            if (d2) d2.readOnly = false
          } catch (e) {}
        }, restoreDelay)
      }, initialDelay)
    } catch (error) {}

    // 返回 cleanup
    return () => {
      if (t1) window.clearTimeout(t1)
      if (t2) window.clearTimeout(t2)
    }
  }

  const suppressEnterModalClose = (duration = 250) => {
    suppressEnterModalCloseUntilRef.current = Date.now() + duration
  }

  // 页面初始化：只显示第一页，聚焦箱号输入框，监听键盘事件
  useEffect(() => {
    setPageIndex(0) // 初始化显示第一页
    // 使用 tempFocus 来聚焦并临时抑制键盘弹出
    const cleanup = tempFocus(cartonInputRef, { cursor: 'all' }, 200, 0)
    window.addEventListener('keyup', onWindowKeyUp)
    return () => {
      // 移除全局键盘事件
      window.removeEventListener('keyup', onWindowKeyUp)
      // 清理定时器
      cleanup && cleanup()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 每次pageIndex变化时同步ref
  useEffect(() => {
    pageIndexRef.current = pageIndex
    if (pageIndex === 0) {
      cartonInputRef.current && tempFocus(cartonInputRef, { cursor: 'all' }, 100, 0)
    }
  }, [pageIndex])

  useEffect(() => {
    epcValueRef.current = epcValue
  }, [epcValue])

  /**
   * 全局键盘事件处理，主要用于弹窗关闭和焦点恢复
   * 例如：回车关闭弹窗并恢复输入框焦点
   */
  const onWindowKeyUp = e => {
    const currentPageIndex = pageIndexRef.current
    const currentEpcValue = epcValueRef.current
    const currentScanRoundFlag = scanRoundFlag.current
    if (e.keyCode === 0 && e.code === 'F23') {
      switch (currentPageIndex) {
        case 0:
          break
        case 1:
          break
        case 2:
          if (!currentScanRoundFlag) {
            getRoundScanResultFun()
          }
          break
        case 3:
          if (currentEpcValue === '') {
            getEpcValueFun()
          }
          break
        default:
          break
      }
      return
    }
    if (e.keyCode === 13) {
      if (Date.now() < suppressEnterModalCloseUntilRef.current) {
        return
      }
      // 关闭 错误提示弹窗
      if (errorUpcModal) {
        destroyModal(errorUpcModal)
        errorUpcModal = null
        upcInputRef.current && tempFocus(upcInputRef, { cursor: 'all' }, 200, 0)
        errorNum = 0
      }
      // 关闭 错误提示弹窗
      if (errorQrCodeModal) {
        destroyModal(errorQrCodeModal)
        errorQrCodeModal = null
        qrCodeInputRef.current && tempFocus(qrCodeInputRef, { cursor: 'all' }, 200, 0)
        errorNum = 0
      }
      // 关闭 错误提示弹窗-carton
      if (errorCartonModal) {
        destroyModal(errorCartonModal)
        errorCartonModal = null
        cartonInputRef.current && tempFocus(cartonInputRef, { cursor: 'all' }, 200, 0)
        errorNum = 0
      }
      // 关闭 错误提示弹窗-不需要获取焦点
      if (errorModal) {
        destroyModal(errorModal)
        errorModal = null
        errorNum = 0
      }
      // 关闭 成功提示弹窗
      if (successModal) {
        destroyModal(successModal)
        successModal = null
        errorNum = 0
      }
    }
  }

  /**
   * 清空carton NO 回车标志
   * 箱号清空时重置所有相关状态
   */
  useEffect(() => {
    if (!cartonNoValue) {
      setUpcValue('')
      setEpcValue('')
      setQrCodeValue('')
      setScanCompareResult('')
      setTotalCount(0)
      setScanResultList([])
      setCartonEnterFlag(false)
      setCartonDisableFlag(false)
      setScanResultShowFlag(false)
      setScanRoundResultShowFlag(false)
      scanRoundFlag.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartonNoValue])

  /**
   * 获取环扫结果并处理，最终关闭环扫
   * 业务流程：获取EPC列表 -> 提交接口 -> 展示结果
   */
  const getRoundScanResultFun = () => {
    let shouldStopRoundScan = true
    console.log(cartonNoValueRef.current, 'cartonNo in round scan One')
    getRoundScanResult()
      .then(res => {
        console.log(cartonNoValueRef.current, 'cartonNo in round scan Two')
        const roundScanValue = res?.resultData?.split(',')
        if (!roundScanValue || roundScanValue.length === 0 || (roundScanValue.length === 1 && !roundScanValue[0])) {
          let str = formatMessageNew('languageObj.qrCode.noRoundScanData')
          errorModal = modalError({ content: str }, () => {
            errorModal = null
          })
          shouldStopRoundScan = false
          return
        }
        if (!validateScanContext()) return
        let params = {
          cartonNo: cartonNoValueRef.current,
          businessType,
          plant,
          messageId: `${Date.now()}_${Math.random()
            .toString(36)
            .substr(2, 9)}`,
          scanInfoList: roundScanValue,
          deviceCode: resolvedDeviceCode,
          statusMessage: '读取成功',
          statusCode: '100',
          scanTime: Date.now()
        }
        console.log(cartonNoValueRef.current, 'cartonNo in round scan Three')
        console.log(params, 'params in round scan')
        manualRoundScanByCarton(params, queryHeader)
          .then(res => {
            setRoundScanResultList(res.resultData.scanResultList)
            setScanCompareResult(res.resultData.scanCompareResult)
            setScanRoundResultShowFlag(true)
            scanRoundFlag.current = true
          })
          .catch(err => {
            if (err.errorCode === '7006') {
              if (errorNum !== 0) return
              errorNum = 1
              const str =
                formatMessageNew('languageObj.qrCode.errorcode_' + err.errorCode) ||
                err.errorMessage ||
                formatMessageNew('languageObj.qrCode.noRoundScanData')
              errorModal = modalError({ content: str }, () => {
                errorModal = null
                errorNum = 0
              })
              return
            }
            console.log(err)
          })
      })
      .catch(err => {
        if (errorNum !== 0) return
        errorNum = 1
        let str = formatMessageNew('languageObj.qrCode.readEpcErr')
        errorModal = modalErrorRefreshCode({ content: str }, () => {
          errorModal = null
          errorNum = 0
        })
      })
      .finally(() => {
        if (shouldStopRoundScan) {
          // 旧逻辑保留：stopRoundScan(queryHeader)
          stopRoundScanWithPower()
        }
      })
  }

  /**
   * 获取EPC值并查SKU
   * 业务流程：读取EPC -> 查SKU -> 更新产品列表
   */
  const getEpcValueFun = () => {
    setEpcValue('')
    readRfid()
      .then(res => {
        const value = res?.resultData?.rfid
        const epc = value === 'null' ? '' : value
        setEpcValue(epc)
        if (epc) {
          getSkuInfoByEpcFun(epc) // 只用参数，不用 state
        }
        resetRfid()
      })
      .catch(err => {
        if (errorNum !== 0) return
        errorNum = 1
        let str = formatMessageNew('languageObj.qrCode.readEpcErr')
        errorModal = modalErrorRefreshCode({ content: str }, () => {
          errorModal = null
          errorNum = 0
        })
      })
  }

  /**
   * 输入框内容变化处理，按类型分别处理
   * type: carton/epc/upc/qr
   */
  const onInputChange = (e, type) => {
    const value = e.target.value.trim()
    const index = value.indexOf('/n')
    const newValue = index > -1 ? value.substring(0, index) : value
    if (type === 'carton') {
      setCartonNoValue(newValue)
    }
    if (type === 'epc') {
      setEpcValue(newValue)
    }
    if (type === 'upc') {
      setUpcValue(newValue)
    }
    if (type === 'qr') {
      setQrCodeValue(newValue)
    }
  }

  /**
   * 输入框回车事件处理，按类型分别触发接口
   * type: carton/epc/upc/qr
   */
  const onInputKeyDown = (e, type) => {
    if (e.keyCode === 13) {
      if (type === 'carton') {
        getCartonInfo()
      }
      if (type === 'epc') {
        // 检验upc
        getSkuInfoByEpcFun()
      }
      if (type === 'upc') {
        // 检验upc
        getSkuInfoByUpcFun()
      }
      if (type === 'qr') {
        // 检验Qr Code
        getSkuInfoByQRFun()
      }
    }
  }

  // cartonNoValueRef 用于异步场景下获取最新箱号
  const cartonNoValueRef = useRef(cartonNoValue)
  useEffect(() => {
    cartonNoValueRef.current = cartonNoValue
  }, [cartonNoValue])

  /**
   * 根据箱号获取订单信息，进入普通扫描流程
   * 业务流程：提交箱号 -> 获取订单SKU列表 -> 展示结果
   */
  const getCartonInfo = useCallback(() => {
    // 检查箱号是否为空
    if (!cartonNoValueRef.current || cartonNoValueRef.current.trim() === '') {
      if (errorNum === 0) {
        errorNum = 1
        cartonInputRef.current && cartonInputRef.current.blur()
        suppressEnterModalClose()
        errorCartonModal = modalErrorRefreshCode(
          {
            content: formatMessageNew('languageObj.qrCode.cartonNoEmpty') || 'Carton No cannot be empty'
          },
          () => {
            tempFocus(cartonInputRef, { cursor: 'all' }, 200, 0)
            errorNum = 0
            errorCartonModal = null
          }
        )
      }
      return
    }

    if (!validateScanContext()) return

    let params = {
      cartonNo: cartonNoValueRef.current, // 用 ref 取值
      businessType,
      plant,
      deviceCode: resolvedDeviceCode
    }
    getScanInfoByCarton(params, queryHeader)
      .then(res => {
        setScanResultList(res.resultData.scanResultList)
        setScanCompareResult(res.resultData.scanCompareResult)
        setTotalCount(res.resultData.totalAsnQty)
        if (res.resultData.scanResultList.length > 0) {
          setCartonEnterFlag(true)
        } else {
          setCartonDisableFlag(true)
        }
        setScanResultShowFlag(true)
        setInputReadOnly(true)
      })
      .catch(err => {
        let str = formatMessageNew('languageObj.qrCode.errorcode_' + err.errorCode)
        if (errorNum === 0) {
          errorNum = 1
          cartonInputRef.current.blur()
          suppressEnterModalClose()
          errorCartonModal = modalErrorRefreshCode(
            {
              content: str || err.errorMessage
            },
            () => {
              tempFocus(cartonInputRef, { cursor: 'all' }, 200, 0)
              errorNum = 0
              errorCartonModal = null
            }
          )
        }
      })
  }, [businessType, plant, resolvedDeviceCode, queryHeader, validateScanContext])

  const updateEpcBoundScanProductList = (currentEpcValue, resultData = []) => {
    const epcList = (currentEpcValue || '').split(',').filter(e => e)
    setEpcBoundRollbackEpcList(epcList)
    setEpcBoundOriginProductList(scanProductList.filter(item => epcList.includes(item.epc)).map(item => ({ ...item })))
    const validList = Array.isArray(resultData) ? resultData : resultData ? [resultData] : []
    let defaultReworkOperationType = null
    const getDefaultReworkOperationType = () => {
      if (defaultReworkOperationType !== null) return defaultReworkOperationType
      const mergedEpcMap = validList.map(e => e?.epc).filter(e => e)
      const multiSameEpcFlag = mergedEpcMap.length > 1 ? 1 : 0
      const physicalSkuCorrectFlag = manualScanVisibleFlag ? 1 : 0
      if (multiSameEpcFlag === 0) {
        defaultReworkOperationType = 2
      } else if (multiSameEpcFlag === 1) {
        defaultReworkOperationType = physicalSkuCorrectFlag === 1 ? 3 : 1
      } else {
        defaultReworkOperationType = 0
      }
      return defaultReworkOperationType
    }
    const scanedProductList = epcList.map(epc => {
      const suggestedReworkOperationType =
        validList.find(item => item?.epc === epc)?.suggestedReworkOperationType ??
        validList.find(item => item?.suggestedReworkOperationType !== undefined)?.suggestedReworkOperationType
      return {
        epc,
        ignoreFlag: validList.length === 0 || validList.some(e => e?.epc === epc) ? 0 : 1,
        reworkOperationType: suggestedReworkOperationType ?? getDefaultReworkOperationType(),
        upc: validList.find(item => item?.epc === epc)?.upc || '',
        relabelMixFlag: 1
      }
    })
    setScanProductList(prevList => {
      const filteredScanProductList = prevList.filter(item => !epcList.includes(item.epc))
      return [...filteredScanProductList, ...scanedProductList]
    })
  }

  const markEpcBoundRelabelMixFlag = (currentEpcValue, resultData = []) => {
    const epcList = (currentEpcValue || '').split(',').filter(e => e)
    const validList = Array.isArray(resultData) ? resultData : resultData ? [resultData] : []
    setEpcBoundRollbackEpcList([])
    setEpcBoundOriginProductList(scanProductList.filter(item => epcList.includes(item.epc)).map(item => ({ ...item })))
    const nextScanProductList = scanProductList.map(item => {
      if (!epcList.includes(item.epc)) return item
      const suggestedReworkOperationType =
        validList.find(validItem => validItem?.epc === item.epc)?.suggestedReworkOperationType ??
        validList.find(validItem => validItem?.suggestedReworkOperationType !== undefined)?.suggestedReworkOperationType
      return {
        ...item,
        reworkOperationType: suggestedReworkOperationType ?? item.reworkOperationType,
        relabelMixFlag: 1
      }
    })
    setScanProductList(nextScanProductList)
  }

  const updateAlreadyMergedScanProductList = (currentEpcValue, resultData = []) => {
    const epcList = (currentEpcValue || '').split(',').filter(e => e)
    const validList = Array.isArray(resultData) ? resultData : resultData ? [resultData] : []
    const firstValidItem = validList[0] || {}
    const mergedEpc = validList.find(item => item?.mergedEpc)?.mergedEpc || ''
    const hasSameEpc = epcList.some(epc => scanProductList.some(item => item.epc === epc))
    if (!hasSameEpc) {
      setReScanFlag(false)
    }
    setSkuValueByEpc(firstValidItem.sku || '')
    setManualScanStatus(true)
    setManualCountStatus(true)
    setManualStatus(false)
    setScanProductList(prevList => {
      const filteredScanProductList = prevList.filter(item => !epcList.includes(item.epc))
      const alreadyMergedProductList = epcList.map(epc => {
        const matchedItem = validList.find(item => item?.epc === epc) || {}
        return {
          epc,
          ignoreFlag: 0,
          upc: matchedItem.upc || firstValidItem.upc || '',
          reworkOperationType: 0,
          mergedEpc: matchedItem.mergedEpc || mergedEpc
        }
      })
      return [...filteredScanProductList, ...alreadyMergedProductList]
    })
  }

  /**
   * 根据EPC获取订单详细信息，进入手动扫码流程
   * 业务流程：提交EPC -> 获取SKU -> 展示结果或弹窗
   */
  const getSkuInfoByEpcFun = value => {
    if (!validateScanContext()) return
    let currentEpcValue = value ? value : epcValue
    let params = {
      cartonNo: cartonNoValueRef.current,
      businessType: businessType,
      plant: plant,
      epc: currentEpcValue
    }
    getSkuInfoByEpc(params, queryHeader)
      .then(res => {
        if (res.errorCode === '200') {
          if (
            Array.isArray(res.resultData) &&
            res.resultData.length > 0 &&
            res.resultData.every(item => item?.alreadyMerged)
          ) {
            updateAlreadyMergedScanProductList(currentEpcValue, res.resultData)
            return
          }
          // 先过滤掉已有的epc
          const epcList = currentEpcValue.split(',').filter(e => e)
          // 只有不存在时才+1
          if (!scanProductList.some(item => item.epc === res.resultData.epc)) {
            setReScanFlag(false)
          }
          setSkuValueByEpc(res.resultData.sku)
          setManualScanStatus(true)
          setManualCountStatus(true)
          setManualStatus(false)
          if (res.resultData.epc !== currentEpcValue) {
            // 多个 EPC，区分主 EPC 和其他
            const manualProductList = epcList.map(epc => ({
              epc,
              ignoreFlag: epc === res.resultData.epc ? 0 : 1,
              upc: epc === res.resultData.epc ? res.resultData.upc : '',
              reworkOperationType: 0
            }))
            setScanProductList(prevList => {
              const filteredScanProductList = prevList.filter(item => !epcList.includes(item.epc))
              return [...filteredScanProductList, ...manualProductList]
            })
          } else {
            // 只有一个 EPC
            setScanProductList(prevList => {
              const filteredScanProductList = prevList.filter(item => item.epc !== currentEpcValue)
              return [
                ...filteredScanProductList,
                {
                  epc: currentEpcValue,
                  ignoreFlag: 0,
                  upc: currentEpcValue === res.resultData.epc ? res.resultData.upc : '',
                  reworkOperationType: 0
                }
              ]
            })
          }
        }
      })
      .catch(err => {
        if (err.errorCode === '7000') {
          setEpcValue('')
          // 未找到对应的SKU,存储到scanProductList
          const epcList = currentEpcValue.split(',').filter(e => e)
          const scanedProductList = epcList.map(epc => ({ epc, ignoreFlag: 1, reworkOperationType: 0 }))
          setScanProductList(prevList => {
            const filteredScanProductList = prevList.filter(item => !epcList.includes(item.epc))
            return [...filteredScanProductList, ...scanedProductList]
          })
        }

        if (err.errorCode === '7001') {
          // 未找到对应的SKU, 存储到scanProductList 并显示弹窗
          setScanUpcQrModalVisible(true)
          setValidEpcList(err.resultData || [])
          // 延时等待 Modal 挂载后再聚焦，且在聚焦前把 DOM 设为只读以尽量抑制移动端键盘
          setTimeout(() => {
            if (!qrCodeInputRef.current) return
            try {
              const dom = qrCodeInputRef.current.input || qrCodeInputRef.current
              if (dom) dom.readOnly = true
            } catch (e) {
              // ignore
            }
            // 把 restoreDelay 调大（例如 600ms），initialDelay 设为 0
            tempFocus(qrCodeInputRef, { cursor: 'all' }, 600, 0)
          }, 80)
        }
        if (err.errorCode === '7002') {
          // 未找到对应的SKU,存储到scanProductList
          const resultData = err.resultData || []
          setValidEpcList(resultData)
          const epcList = currentEpcValue.split(',').filter(e => e)
          const scanedProductList = epcList.map(epc => {
            const epcInfo = resultData.find(item => item.epc === epc)
            const suggestedReworkOperationType = epcInfo?.suggestedReworkOperationType
            return {
              epc,
              ignoreFlag: epcInfo ? 0 : 1,
              reworkOperationType: Number.isInteger(suggestedReworkOperationType)
                ? suggestedReworkOperationType
                : epcInfo?.belongsToCurrentCarton === true
                ? 0
                : 4,
              upc: epcInfo?.upc || ''
            }
          })
          setScanProductList(prevList => {
            const filteredScanProductList = prevList.filter(item => !epcList.includes(item.epc))
            return [...filteredScanProductList, ...scanedProductList]
          })
          setPhysicalCheckRollbackEpcList(prevList => [...new Set([...prevList, ...epcList])])
          setPhysicalSkuCorrectVisible(true)
        }
        if (err.errorCode === '7007') {
          updateEpcBoundScanProductList(currentEpcValue, err.resultData)
          openEpcBoundRelabelFlow('epc')
        }
      })
  }

  /**
   * 根据UPC获取订单详细信息
   */
  const getSkuInfoByUpcFun = () => {
    if (!validateScanContext()) return
    let params = {
      cartonNo: cartonNoValueRef.current,
      businessType: businessType,
      plant: plant,
      upc: upcValue
    }
    getSkuInfoByUpc(params, queryHeader)
      .then(res => {
        if (res.errorCode === '200') {
          setSkuValueByUpc(res.resultData)
          setDialogConfirmVisible(false)
          setSkuValueByEpc(res.resultData)
        }
      })
      .catch(err => {
        setDialogConfirmVisible(true)
        setSkuValueByUpc('')
        if (err.errorCode === '435') {
          modalError(
            {
              content: formatMessageNew('languageObj.qrCode.invalidUpc')
            },
            () => {
              tempFocus(upcInputRef, { cursor: 'all' }, 200, 0)
              errorNum = 0
            }
          )
        }
      })
  }

  /**
   * 根据二维码获取SKU信息
   */
  const getSkuInfoByQRFun = () => {
    if (!validateScanContext()) return
    let params = {
      cartonNo: cartonNoValueRef.current,
      businessType: businessType,
      plant: plant,
      qrCode: qrCodeValue,
      epc: epcValue
    }
    getSkuInfoByQRCode(params, queryHeader)
      .then(res => {
        if (res.errorCode === '200') {
          setSkuValueByQr(res.resultData)
          setQrCodeSkuFetched(true)
          upcInputRef.current && tempFocus(upcInputRef, { cursor: 'all' }, 200, 0)
        }
      })
      .catch(err => {
        setSkuValueByQr('')
        if (err.errorCode === '5601') {
          modalError(
            {
              content: formatMessageNew('languageObj.qrCode.invalidQrCode')
            },
            () => {
              tempFocus(qrCodeInputRef, { cursor: 'all' }, 200, 0)
              errorNum = 0
            }
          )
        }
        if (err.errorCode === '7007') {
          updateEpcBoundScanProductList(epcValueRef.current || epcValue, err.resultData)
          openEpcBoundRelabelFlow('qr')
        }
      })
  }

  /**
   * 校验手动扫码信息，人工校验弹窗确认后调用
   * 业务流程：提交upc和qrCode -> 校验 -> 展示结果
   */
  const validateManualFun = () => {
    if (!validateScanContext()) return
    let params = {
      cartonNo: cartonNoValueRef.current,
      businessType: businessType,
      plant: plant,
      qrCode: qrCodeValue,
      upc: upcValue
    }
    validQRCodeAndUpc(params, queryHeader)
      .then(res => {
        if (res.errorCode === '200') {
          // 收集所有新数据
          const mergedEpcMap = validEpcList.map(e => e.epc)
          const epcList = epcValue.split(',').filter(e => e)
          // 判断返回的epc是否已存在
          const hasSameEpc = validEpcList.some(validItem =>
            scanProductList.some(scanItem => scanItem.epc === validItem.epc && scanItem.reworkOperationType !== 4)
          )
          if (!hasSameEpc) {
            setReScanFlag(false)
          }
          const mergedEpc = mergedEpcMap.join(',')
          const multiSameEpcFlag = mergedEpcMap.length > 1 ? 1 : 0
          const physicalSkuCorrectFlag = manualScanVisibleFlag ? 1 : 0
          let reworkOperationType = 0
          if (multiSameEpcFlag === 0) {
            reworkOperationType = 2
          } else if (multiSameEpcFlag === 1) {
            reworkOperationType = physicalSkuCorrectFlag === 1 ? 3 : 1
          }
          const manualProductList = epcList.map(epc => ({
            epc,
            upc: upcValue,
            qrCode: qrCodeValue,
            reworkOperationType: reworkOperationType,
            ignoreFlag: mergedEpcMap.some(e => e === epc) ? 0 : 1,
            mergedEpc
          }))
          setManualStatus(true)
          setScanUpcQrModalVisible(false)
          setManualScanStatus(true)
          setManualScanVisibleFlag(false)
          setManualCountStatus(true)
          setScanProductList(prevList => {
            const filteredScanProductList = prevList.filter(
              item => !epcList.includes(item.epc) && item.reworkOperationType !== 4
            )
            return [...filteredScanProductList, ...manualProductList]
          })
          setPhysicalCheckRollbackEpcList([])
          setValidEpcList([])
          setScanUpcQrModalVisible(false)
          setQrCodeValue('')
          setUpcValue('')
          setSkuValueByQr('')
          setSkuValueByUpc('')
          setManualScanVisibleFlag(false) // 清除弹窗可见标志
          setValidEpcList([]) // 清除无效EPC列表
          setPhysicalSkuCorrectBtnActive(false)
          setPhysicalSkuInCorrectBtnActive(false)
        }
      })
      .catch(err => {
        if (err.errorCode === '5601') {
          modalError(
            {
              content: formatMessageNew('languageObj.qrCode.invalidQrCode')
            },
            () => {
              errorNum = 0
            }
          )
        }
        if (err.errorCode === '435') {
          modalError(
            {
              content: formatMessageNew('languageObj.qrCode.invalidUpc')
            },
            () => {
              errorNum = 0
            }
          )
        }
        if (err.errorCode === '7003') {
          modalError(
            {
              content: formatMessageNew('languageObj.qrCode.upcUnmatched')
            },
            () => {
              errorNum = 0
            }
          )
        }
        if (err.errorCode === '30009') {
          modalError(
            {
              content: formatMessageNew('languageObj.qrCode.noExistInCarton')
            },
            () => {
              errorNum = 0
            }
          )
        }
      })
  }

  /**
   * 输入框获取焦点时只读处理，防止误操作
   */
  const onInputFocus = e => {
    try {
      setInputReadOnly(true)
      setTimeout(() => {
        setInputReadOnly(false)
      }, 100)
    } catch (error) {}
  }

  /**
   * 返回登录首页
   */
  const backFirstPage = () => {
    history.push('/rf/login')
  }

  /**
   * 打开第二页（openCarton），显示扫描结果
   * type为'scan'时展示普通扫描结果，为'roundScan'时展示环扫结果
   */
  const toOpenCartonPage = type => {
    // 如果 type 是 'scan'，显示普通扫描结果，否则显示环扫结果
    if (type === 'scan') {
      setScanResultList(scanResultList)
    } else if (type === 'backScan') {
      setScanResultList(scanResultList)
      setScanProductList(() => [])
      backToManualScanPageData()
    } else {
      setScanResultList(roundScanResultList)
    }
    // 切换到第二页
    setPageIndex(1)
    // 隐藏扫描结果显示
    setScanResultShowFlag(false)
  }

  /**
   * 打开第三页（toRoundCartonPage），进入环扫流程
   */
  const toRoundCartonPage = () => {
    cartonNoValueRef.current = cartonNoValue
    setPageIndex(2) // 切换到第三页
  }

  /**
   * 打开第四页（手动扫码页面）
   * 记录箱号快照，聚焦epc输入框
   */
  const toManualScanPage = useCallback(() => {
    if (!validateScanContext()) return
    cartonNoValueRef.current = cartonNoValue
    // 使用 tempFocus 替代手动 focus + readOnly hack
    tempFocus(epcInputRef, { cursor: 'all' }, 200, 0)
    getCartonInfo()
    // 刷新全局时间戳，标记进入手动扫码页面的时间
    const ts = Date.now()
    manualScanTimestampRef.current = ts
    if (typeof window !== 'undefined') {
      window.relabelReworkTime = ts
    }
    setPageIndex(3)
  }, [cartonNoValue, getCartonInfo, validateScanContext])

  /**
   * 重置环扫页面数据并重新开始环扫
   */
  const resetRoundCartonPage = () => {
    setScanCompareResult('')
    setScanRoundResultShowFlag(false)
    scanRoundFlag.current = false
    setRoundScanResultList([])
    // 旧逻辑保留：startRoundScan && startRoundScan(queryHeader)
    startRoundScanWithPower()
  }

  /**
   * nextCarton：回到第一页并重置所有状态
   * 业务流程：清空所有输入和结果，聚焦箱号输入框
   */
  const closeCartonPage = useCallback(() => {
    setPageIndex(0)
    setScanResultShowFlag(false)
    setCartonNoValue('')
    setUpcValue('')
    setEpcValue('')
    setQrCodeValue('')
    setScanCompareResult('')
    setTotalCount(0)
    setScanResultList([])
    setCartonEnterFlag(false)
    setCartonDisableFlag(false)
    setActiveIndex(null)
    setSkuValueByEpc('')
    setSkuValueByUpc('')
    setSkuValueByQr('')
    setScanCount(0)
    setManualStatus(false)
    setManualScanStatus(false)
    setManualCountStatus(false)
    setScanUpcQrModalVisible(false)
    setPhysicalSkuCorrectVisible(false)
    setPhysicalSkuInCorrectVisible(false)
    setEpcBoundRelabelVisible(false)
    setEpcBoundSubmitVisible(false)
    setEpcBoundReturnSource('')
    setEpcBoundRollbackEpcList([])
    setPhysicalCheckRollbackEpcList([])
    setEpcBoundOriginProductList([])
    setManualScanVisibleFlag(false)
    setDialogConfirmVisible(true)
    setQrCodeSkuFetched(false)
    setManualScanResultInvalidVisible(false)
    setScanProductList(() => [])
    setInvalidList([])
    setReScanFlag(true)
    setPhysicalSkuCorrectBtnActive(false)
    setPhysicalSkuInCorrectBtnActive(false)
    setTimeout(() => {
      cartonInputRef.current && tempFocus(cartonInputRef, { cursor: 'all' }, 100, 0)
    }, 100)
  }, [])

  /**
   * 手动关闭并提交
   */
  // const manualCloseCartonPage = comfirmType => {
  //   submitManualScanFun(comfirmType)
  //   closeCartonPage()
  // }

  /**
   * 重置重扫页面数据
   * 业务流程：清空输入和弹窗，聚焦epc输入框
   */
  const reScanPageData = () => {
    // 分割epcValue，得到epc列表
    const epcList = epcValue.split(',').filter(e => e)
    // 过滤掉scanProductList中epc匹配上的元素
    setScanProductList(prevList => prevList.filter(item => !epcList.includes(item.epc)))
    setUpcValue('')
    setEpcValue('')
    setQrCodeValue('')
    setActiveIndex(null)
    setSkuValueByUpc('')
    setSkuValueByQr('')
    setPhysicalSkuCorrectBtnActive(false)
    setPhysicalSkuInCorrectBtnActive(false)
    setManualStatus(false)
    setManualScanStatus(false)
    // DialogMobile弹窗相关
    setScanUpcQrModalVisible(false)
    setPhysicalSkuCorrectVisible(false)
    setPhysicalSkuInCorrectVisible(false)
    setEpcBoundRelabelVisible(false)
    setEpcBoundSubmitVisible(false)
    setEpcBoundReturnSource('')
    setEpcBoundRollbackEpcList([])
    setPhysicalCheckRollbackEpcList([])
    setEpcBoundOriginProductList([])
    setManualScanVisibleFlag(false)
    setDialogConfirmVisible(true)
    setQrCodeSkuFetched(false)
    setValidEpcList([])
    setRelabelVisible(false)
    setShowReprint(false)
    setRelabelValue(null)
    setNoRfidTagActive(false)
    setScanNoResponseActive(false)
    setManualScanResultInvalidVisible(false)
    setScanCount(scanCount - 1)
    setInvalidList([])
    setReScanFlag(true)
    setTimeout(() => {
      epcInputRef.current && tempFocus(epcInputRef, { cursor: 'all' }, 100, 0)
    }, 100)
  }

  /**
   * 重扫下一件
   * 业务流程：清空epc相关输入和弹窗，聚焦epc输入框
   */
  const reScanNextPiece = () => {
    //？？？？？？？？？？？？是否需要清空ScanProductList
    // 页面主数据
    setUpcValue('')
    setEpcValue('')
    setQrCodeValue('')
    setActiveIndex(null)
    setSkuValueByUpc('')
    setSkuValueByQr('')
    setPhysicalSkuCorrectBtnActive(false)
    setPhysicalSkuInCorrectBtnActive(false)
    setManualStatus(false)
    setManualScanStatus(false)
    // DialogMobile弹窗相关
    setScanUpcQrModalVisible(false)
    setPhysicalSkuCorrectVisible(false)
    setPhysicalSkuInCorrectVisible(false)
    setEpcBoundRelabelVisible(false)
    setEpcBoundSubmitVisible(false)
    setEpcBoundReturnSource('')
    setEpcBoundRollbackEpcList([])
    setPhysicalCheckRollbackEpcList([])
    setEpcBoundOriginProductList([])
    setManualScanVisibleFlag(false)
    setDialogConfirmVisible(true)
    setQrCodeSkuFetched(false)
    setValidEpcList([])
    setRelabelVisible(false)
    setShowReprint(false)
    setRelabelValue(null)
    setNoRfidTagActive(false)
    setScanNoResponseActive(false)
    setManualScanResultInvalidVisible(false)
    setInvalidList([])
    setReScanFlag(true)
    setTimeout(() => {
      epcInputRef.current && tempFocus(epcInputRef, { cursor: 'all' }, 100, 0)
    }, 100)
  }

  /**
   * 环扫页面进入时，自动开始环扫，离开时停止环扫
   */
  useEffect(() => {
    if (pageIndex === 2) {
      // 旧逻辑保留：startRoundScan && startRoundScan(queryHeader)
      startRoundScanWithPower()
    }
    return () => {
      if (pageIndex === 2) {
        // 旧逻辑保留：stopRoundScan && stopRoundScan(queryHeader)
        stopRoundScanWithPower()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex])

  /**
   * 人工校验接口
   * 业务流程：提交人工校验类型 -> 接口处理
   */
  const manualCheckFun = useCallback(
    manualCheckType => {
      if (!validateScanContext()) return
      let params = {
        cartonNo: cartonNoValueRef.current,
        businessType: businessType,
        plant: plant,
        manualCheckInfo: manualCheckType
      }
      manualCheckInfoByCarton(params, queryHeader)
        .then(() => {})
        .catch(err => {
          if (errorNum === 0) {
            errorNum = 1
            cartonInputRef.current.blur()
            errorModal = modalError(
              {
                content: formatMessageNew('languageObj.qrCode.errorcode_' + err.errorCode)
              },
              () => {
                tempFocus(cartonInputRef, { cursor: 'all' }, 200, 0)
                errorNum = 0
              }
            )
          }
        })
    },
    [businessType, plant, queryHeader, validateScanContext]
  )

  /**
   * 二页按钮点击处理，弹窗确认后执行人工校验
   * idx为按钮索引，人工校验类型
   */
  const handleBtnClick = useCallback(
    idx => {
      if (idx === 4) {
        //to manual Check Scan
        toManualScanPage()
        return
      }
      setActiveIndex(idx)
      modalConfirm(
        {
          content: <span dangerouslySetInnerHTML={{ __html: formatMessageNew(msgKeys[idx - 1]) }} />,
          onCancel: () => setActiveIndex(null)
        },
        () => {
          setActiveIndex(null)
          // 弹窗先关闭，再异步处理
          setTimeout(() => {
            manualCheckFun(idx)
            closeCartonPage()
          }, 0)
        }
      )
    },
    [closeCartonPage, manualCheckFun, msgKeys, toManualScanPage]
  )

  /**
   * 清除手动扫码弹窗相关状态
   */
  const clearManualScanModal = () => {
    if (manualScanVisibleFlag) {
      setPhysicalSkuCorrectVisible(true)
    } else {
      setEpcValue('')
      setValidEpcList([]) // 清除无效EPC列表
    }
    setScanUpcQrModalVisible(false)
    setQrCodeValue('')
    setUpcValue('')
    setSkuValueByQr('')
    setSkuValueByUpc('')
    setManualScanVisibleFlag(false) // 清除弹窗可见标志
    setPhysicalSkuCorrectBtnActive(false)
    setPhysicalSkuInCorrectBtnActive(false)
  }

  /**
   * 返回手动扫码页面并重置相关数据
   */
  const backToManualScanPageData = () => {
    // 页面主数据 //???????是否需要清空ScanProductList
    // 页面主数据
    setUpcValue('')
    setEpcValue('')
    setQrCodeValue('')
    setActiveIndex(null)
    setSkuValueByUpc('')
    setSkuValueByQr('')
    setManualStatus(false)
    setManualScanStatus(false)
    // DialogMobile弹窗相关
    setScanUpcQrModalVisible(false)
    setPhysicalSkuCorrectVisible(false)
    setPhysicalSkuInCorrectVisible(false)
    setEpcBoundRelabelVisible(false)
    setEpcBoundSubmitVisible(false)
    setEpcBoundReturnSource('')
    setEpcBoundRollbackEpcList([])
    setPhysicalCheckRollbackEpcList([])
    setEpcBoundOriginProductList([])
    setManualScanVisibleFlag(false)
    setDialogConfirmVisible(true)
    setQrCodeSkuFetched(false)
    setValidEpcList([])
    setRelabelVisible(false)
    setShowReprint(false)
    setRelabelValue(null)
    setNoRfidTagActive(false)
    setScanNoResponseActive(false)
    setManualScanResultInvalidVisible(false)
    setManualStatus(false)
    setManualScanStatus(false)
    setManualCountStatus(false)
    setScanCount(0)
    setInvalidList([])
    setPhysicalSkuCorrectBtnActive(false)
    setPhysicalSkuInCorrectBtnActive(false)
    setTimeout(() => {
      epcInputRef.current && tempFocus(epcInputRef, { cursor: 'all' }, 100, 0)
    }, 100)
  }

  /**
   * 回滚本次实物SKU校验待确认的EPC
   */
  const rollbackPhysicalCheckScanProductList = () => {
    if (physicalCheckRollbackEpcList.length === 0) return
    setScanProductList(prevList => prevList.filter(item => !physicalCheckRollbackEpcList.includes(item.epc)))
    setPhysicalCheckRollbackEpcList([])
  }

  /**
   * 清除物理正确弹窗相关状态
   */
  const clearPhysicalCorrectModal = () => {
    rollbackPhysicalCheckScanProductList()
    setEpcValue('')
    setPhysicalSkuCorrectVisible(false)
    setQrCodeValue('')
    setUpcValue('')
    setSkuValueByQr('')
    setSkuValueByUpc('')
    setPhysicalSkuCorrectBtnActive(false)
    setPhysicalSkuInCorrectBtnActive(false)
  }

  /**
   * 清除物理错误弹窗相关状态
   */
  const clearPhysicalInCorrectModal = () => {
    setPhysicalSkuInCorrectVisible(false)
    setQrCodeValue('')
    setUpcValue('')
    setSkuValueByQr('')
    setSkuValueByUpc('')
    setPhysicalSkuCorrectBtnActive(false)
    setPhysicalSkuInCorrectBtnActive(false)
    setPhysicalSkuCorrectVisible(true)
  }

  /**
   * 物理正确弹窗切换到物理错误弹窗
   */
  const toPhysicalCheckModal = () => {
    if (physicalSkuCorrectBtnActive) {
      // 实物SKU正确, 验证EPC是否可以补标
      validRelabeledEpcFun()
    }
    if (physicalSkuInCorrectBtnActive) {
      setPhysicalSkuCorrectVisible(false)
      setPhysicalSkuInCorrectVisible(true)
    }
  }

  /**
   * 验证EPC是否可以补标
   */
  const validRelabeledEpcFun = () => {
    let params = {
      cartonNo: cartonNoValueRef.current,
      businessType: businessType,
      plant: plant,
      epc: epcValue
    }
    validRelabeledEpc(params, queryHeader)
      .then(res => {
        console.log(res)
        // 如果返回200，就走之前的流程
        if (res.errorCode === '200') {
          setPhysicalSkuCorrectVisible(false)
          setScanUpcQrModalVisible(true)
          // 等待 Modal 挂载后再聚焦，聚焦前把真实 DOM 设为只读以抑制移动端键盘
          setTimeout(() => {
            if (!qrCodeInputRef.current) return
            try {
              const dom = qrCodeInputRef.current.input || qrCodeInputRef.current
              if (dom) dom.readOnly = true
            } catch (e) {
              // ignore
            }
            // 延长恢复只读时间以确保键盘不弹起（restoreDelay = 600ms，initialDelay = 0）
            tempFocus(qrCodeInputRef, { cursor: 'all' }, 600, 0)
          }, 120)
          setManualScanVisibleFlag(true)
        }
      })
      .catch(err => {
        // 如果返回7007，则提示EPC已被产品绑定，请按UPC进行补标
        if (err.errorCode === '7007') {
          markEpcBoundRelabelMixFlag(epcValueRef.current || epcValue, err.resultData)
          openEpcBoundRelabelFlow('physicalCorrect')
        }
      })
  }

  const openEpcBoundRelabelFlow = source => {
    setEpcBoundReturnSource(source)
    setPhysicalSkuCorrectVisible(false)
    setScanUpcQrModalVisible(false)
    setEpcBoundSubmitVisible(false)
    setEpcBoundRelabelVisible(true)
  }

  const restoreEpcBoundReturnSource = () => {
    if (epcBoundReturnSource === 'physicalCorrect') {
      setPhysicalSkuCorrectVisible(true)
    }
    if (epcBoundReturnSource === 'qr') {
      setScanUpcQrModalVisible(true)
      setTimeout(() => {
        qrCodeInputRef.current && tempFocus(qrCodeInputRef, { cursor: 'all' }, 200, 0)
      }, 100)
    }
    if (epcBoundReturnSource === 'epc') {
      setTimeout(() => {
        epcInputRef.current && tempFocus(epcInputRef, { cursor: 'all' }, 100, 0)
      }, 100)
    }
    setEpcBoundReturnSource('')
  }

  const rollbackEpcBoundScanProductList = () => {
    if (epcBoundRollbackEpcList.length === 0) return
    setScanProductList(prevList => [
      ...prevList.filter(item => !epcBoundRollbackEpcList.includes(item.epc)),
      ...epcBoundOriginProductList
    ])
    setEpcBoundRollbackEpcList([])
    setEpcBoundOriginProductList([])
  }

  /**
   * 还原到未点击“实物SKU正确”的状态
   */
  const restorePhysicalCorrectCheckState = () => {
    if (epcBoundOriginProductList.length > 0) {
      setScanProductList(prevList =>
        prevList.map(item => epcBoundOriginProductList.find(origin => origin.epc === item.epc) ?? item)
      )
      setEpcBoundOriginProductList([])
    }
    setPhysicalSkuCorrectBtnActive(false)
    setPhysicalSkuInCorrectBtnActive(false)
  }

  const cancelEpcBoundRelabelModal = () => {
    const shouldEnableReScan = epcBoundReturnSource === 'epc'
    const shouldRollbackScanProductList = epcBoundReturnSource !== 'physicalCorrect'
    setEpcBoundRelabelVisible(false)
    if (shouldRollbackScanProductList) {
      rollbackEpcBoundScanProductList()
    } else {
      setEpcBoundRollbackEpcList([])
      restorePhysicalCorrectCheckState()
    }
    if (shouldEnableReScan) {
      setReScanFlag(false)
    }
    restoreEpcBoundReturnSource()
  }

  const confirmEpcBoundRelabelModal = () => {
    setEpcBoundRelabelVisible(false)
    setEpcBoundSubmitVisible(true)
  }

  const cancelEpcBoundSubmitModal = () => {
    const shouldEnableReScan = epcBoundReturnSource === 'epc'
    const shouldRollbackScanProductList = epcBoundReturnSource !== 'physicalCorrect'
    setEpcBoundSubmitVisible(false)
    if (shouldRollbackScanProductList) {
      rollbackEpcBoundScanProductList()
    } else {
      setEpcBoundRollbackEpcList([])
      restorePhysicalCorrectCheckState()
    }
    if (shouldEnableReScan) {
      setReScanFlag(false)
    }
    restoreEpcBoundReturnSource()
  }

  const confirmEpcBoundSubmitModal = () => {
    setEpcBoundSubmitVisible(false)
    setEpcBoundReturnSource('')
    setEpcBoundRollbackEpcList([])
    setEpcBoundOriginProductList([])
    submitManualScanFun(4)
  }

  /**
   * 切换物理正确/错误按钮状态
   */
  const changeCorrectBtnStatus = type => {
    if (type === 'correct') {
      setPhysicalSkuInCorrectBtnActive(false)
      setPhysicalSkuCorrectBtnActive(true)
    }
    if (type === 'inCorrect') {
      setPhysicalSkuCorrectBtnActive(false)
      setPhysicalSkuInCorrectBtnActive(true)
    }
  }

  // upc输入变化时重置相关状态
  useEffect(() => {
    if (!upcValue) {
      setDialogConfirmVisible(true)
      setSkuValueByUpc('')
    }
  }, [upcValue])

  // 二维码输入变化时重置相关状态
  useEffect(() => {
    if (!qrCodeValue) {
      setSkuValueByQr('')
      setQrCodeSkuFetched(false)
    } else {
      setQrCodeSkuFetched(false)
    }
  }, [qrCodeValue])

  /**
   * 重新贴标弹窗相关处理
   */
  const handleNoRfidTagClick = value => {
    setNoRfidTagActive(true)
    setScanNoResponseActive(false)
    setRelabelValue(value)
  }

  const handleScanNoResponseClick = value => {
    setNoRfidTagActive(false)
    setScanNoResponseActive(true)
    setRelabelValue(value)
  }

  const openReprintPage = () => {
    // 每次打开都生成新的 key，强制 ReprintPage 重建实例
    setReprintKey(Date.now())
    setShowReprint(true)
  }

  /**
   * 关闭重打标签页面
   */
  const closeReprintPage = () => {
    setShowReprint(false)
    setRelabelValue(null)
    setScanNoResponseActive(false)
    setNoRfidTagActive(false)
    setRelabelVisible(true)
  }

  /**
   * 手动扫码提交接口
   */
  const submitManualScanFun = comfirmType => {
    if (!validateScanContext()) return
    let params = {
      commitType: comfirmType,
      isForceCommit: comfirmType === 2 || comfirmType === 3 || comfirmType === 4,
      cartonNo: cartonNoValueRef.current,
      businessType: businessType,
      plant: plant,
      messageId: `${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`,
      deviceCode: resolvedDeviceCode,
      statusMessage: '读取成功',
      statusCode: '100',
      scanTime: Date.now(),
      // 使用组件内 ref 保证与本次 toManualScanPage 操作一致的时间戳
      relabelReworkTime:
        manualScanTimestampRef.current || (typeof window !== 'undefined' ? window.relabelReworkTime : Date.now()),
      scannedEpcInfoList: scanProductList
    }
    manualScanByPiece(params, queryHeader)
      .then(res => {
        if (res.errorCode === '200' && comfirmType !== 4) {
          if (errorNum === 0) {
            errorNum = 1
            successModal = modalSuccess({
              content: formatMessageNew('languageObj.qrCode.cartonCommitSuccess')
            })
            setTimeout(() => {
              errorNum = 0
              successModal = null
              closeCartonPage()
              destroyModal(successModal)
            }, 2000) // 2秒后自动关闭弹窗并执行逻辑
          }
        } else if (res.errorCode === '200' && comfirmType === 4) {
          closeCartonPage()
        }
      })
      .catch(err => {
        if (comfirmType !== 4 && err.errorCode === '7005') {
          if (Array.isArray(err.resultData) && err.resultData.length > 0) {
            // 这里只处理第一个元素，也可以根据需求处理全部
            err.resultData.forEach(item => {
              item.status = item.asnQty - item.rfidScanQty > 0 ? 'missing' : 'exceeds'
              item.invaildCount = item.asnQty - item.rfidScanQty
            })
            setInvalidList(err.resultData)
          }
          setManualScanResultInvalidVisible(true)
        }
      })
  }

  /**
   * 获取手动扫码计数
   */
  const getManualScanCountFun = useCallback(() => {
    let params = {
      scannedEpcInfoList: scanProductList.filter(e => e.ignoreFlag === 0)
    }
    manualScanCount(params, queryHeader)
      .then(res => {
        setScanCount(res.resultData || 0)
      })
      .catch(err => {
        console.log(err)
      })
  }, [scanProductList, queryHeader])

  // 手动扫码计数变化时自动更新
  useEffect(() => {
    if (pageIndexRef.current === 3) {
      getManualScanCountFun()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanProductList])

  // 页面渲染部分，按pageIndex显示不同页面
  // pageIndex=0：箱号输入及普通扫描结果
  // pageIndex=1：扫描结果确认及人工校验
  // pageIndex=2：环扫结果展示
  // pageIndex=3：手动扫码页面
  return (
    <ConfigProvider
      theme={{
        token: {
          fontSize: 14
        }
      }}
    >
      {/* 页面一：箱号输入及扫描结果展示 */}
      {pageIndex === 0 && (
        <div
          style={{ height: 'auto', overflow: 'auto', marginLeft: -5, marginRight: -5 }}
          className="full-tag-wrapper qrcode-wrapper"
        >
          <div>
            <div style={{ marginBottom: 8, fontSize: 16, fontWeight: 700 }}>{formatMessageNew(titleKey)}</div>
            <div className="displayFlex alignCenter mt5">
              <div className="scan_label">{formatMessageNew('languageObj.qrCode.cartonNo')}：</div>
              <div className="scan_label2">
                <Input
                  className="pt5 pb5"
                  ref={cartonInputRef}
                  value={cartonNoValue}
                  allowClear
                  readOnly={inputReadOnly}
                  disabled={cartonEnterFlag || cartonDisableFlag}
                  placeholder={`${formatMessageNew('languageObj.nsptagLocale.pleaseScan')}${formatMessageNew(
                    'languageObj.qrCode.cartonNo'
                  )}`}
                  onFocus={onInputFocus}
                  onKeyDown={e => onInputKeyDown(e, 'carton')}
                  onChange={e => onInputChange(e, 'carton')}
                />
              </div>
            </div>
          </div>
          {scanResultShowFlag && (
            <div style={{ marginTop: 30 }}>
              <h3 style={{ marginBottom: 8 }}>{formatMessageNew('languageObj.qrCode.latestScanResult')}</h3>
              <div style={{ marginBottom: 30 }}>
                <div style={{ width: '100%' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th
                          style={{
                            width: '33.33%',
                            border: '1px solid #ddd',
                            textAlign: 'center',
                            background: '#ffffff'
                          }}
                        >
                          SKU
                        </th>
                        <th
                          style={{
                            width: '33.33%',
                            border: '1px solid #ddd',
                            textAlign: 'center',
                            background: '#ffffff'
                          }}
                        >
                          {formatMessageNew('languageObj.qrCode.asnQty')}
                        </th>
                        <th
                          style={{
                            width: '33.33%',
                            border: '1px solid #ddd',
                            textAlign: 'center',
                            background: '#ffffff'
                          }}
                        >
                          {formatMessageNew('languageObj.qrCode.systemQty')}
                        </th>
                      </tr>
                    </thead>
                  </table>
                  <div style={{ maxHeight: '72px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <tbody>
                        {scanResultList.map((item, idx) => (
                          <tr key={idx}>
                            <td style={{ width: '33.33%', border: '1px solid #ddd', textAlign: 'center' }}>
                              {renderSkuAndSize(item.skuAndSize)}
                            </td>
                            <td style={{ width: '33.33%', border: '1px solid #ddd', textAlign: 'center' }}>
                              {item.asnQty}
                            </td>
                            <td style={{ width: '33.33%', border: '1px solid #ddd', textAlign: 'center' }}>
                              {item.rfidScanQty}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                      <tr>
                        <td
                          style={{
                            width: '33.33%',
                            border: '1px solid #ddd',
                            textAlign: 'center',
                            fontWeight: 'bold',
                            backgroundColor: '#f0f0f0'
                          }}
                        >
                          Total
                        </td>
                        <td
                          style={{
                            width: '33.33%',
                            border: '1px solid #ddd',
                            textAlign: 'center',
                            fontWeight: 'bold',
                            backgroundColor: '#f0f0f0'
                          }}
                        >
                          {scanResultList && scanResultList.length > 0
                            ? scanResultList.reduce((sum, item) => sum + Number(item?.asnQty || 0), 0)
                            : 0}
                        </td>
                        <td
                          style={{
                            width: '33.33%',
                            border: '1px solid #ddd',
                            textAlign: 'center',
                            fontWeight: 'bold',
                            backgroundColor: '#f0f0f0'
                          }}
                        >
                          {scanResultList && scanResultList.length > 0
                            ? scanResultList.reduce((sum, item) => sum + Number(item?.rfidScanQty || 0), 0)
                            : 0}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          {scanResultShowFlag && (
            <div className="displayFlex alignCenter mt8" style={{ marginTop: 30 }}>
              <div
                style={{
                  width: '130px',
                  fontSize: '12px',
                  fontWeight: 600
                }}
              >
                {formatMessageNew('languageObj.qrCode.latestValidationScanResult')}
              </div>
              <div className="scan_label2" style={{ width: '95%' }}>
                <div className="displayFlex">
                  {scanCompareResult && bingResultConfig[scanCompareResult] && (
                    <div style={bingResultConfig[scanCompareResult].style} className="phone-btn2 b1ddd alignC">
                      {formatMessageNew(bingResultConfig[scanCompareResult].key)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          <Row style={{ marginTop: scanResultShowFlag ? 30 : 248 }}>
            <Col className="mt15" style={{ textAlign: 'center' }} span={8}>
              <Button
                style={{ width: '90%' }}
                type="primary"
                className="phone-btn4 reset-btn mt0"
                onClick={backFirstPage}
              >
                {formatMessageNew('languageObj.qrCode.menu')}
              </Button>
            </Col>
            <Col className="mt15" style={{ textAlign: 'center' }} span={8}>
              <Button
                style={{ width: '90%' }}
                type="primary"
                className="phone-btn4 reset-btn mt0"
                onClick={() => toOpenCartonPage('scan')}
                disabled={!(cartonNoValue && cartonEnterFlag)}
              >
                {formatMessageNew('languageObj.qrCode.openCarton')}
              </Button>
            </Col>
            <Col className="mt15" style={{ textAlign: 'center' }} span={8}>
              <Button
                style={{ width: '90%' }}
                type="primary"
                className="phone-btn4 reset-btn mt0"
                onClick={closeCartonPage}
                disabled={!cartonNoValue}
              >
                {formatMessageNew('languageObj.qrCode.nextCarton')}
              </Button>
            </Col>
            <Col className="mt15" style={{ textAlign: 'center' }} span={24}>
              <Button
                style={{ width: '97%' }}
                type="primary"
                className="phone-btn4 reset-btn mt0"
                disabled={!(cartonNoValue && cartonEnterFlag)}
                onClick={toRoundCartonPage}
              >
                {formatMessageNew('languageObj.qrCode.scanEpcs')}
              </Button>
            </Col>
          </Row>
        </div>
      )}
      {/* 页面二：扫描结果确认及人工校验 */}
      {pageIndex === 1 && (
        <div
          style={{ height: 'auto', overflow: 'auto', marginLeft: -5, marginRight: -5 }}
          className="full-tag-wrapper qrcode-wrapper"
        >
          <div>
            <h3 style={{ marginBottom: 8 }}>{formatMessageNew('languageObj.qrCode.scanDataResult')}</h3>
            <div style={{ marginBottom: 30 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th
                      style={{
                        width: '33.33%',
                        border: '1px solid #ddd',
                        textAlign: 'center',
                        background: '#ffffff'
                      }}
                    >
                      SKU
                    </th>
                    <th
                      style={{
                        width: '33.33%',
                        border: '1px solid #ddd',
                        textAlign: 'center',
                        background: '#ffffff'
                      }}
                    >
                      {formatMessageNew('languageObj.qrCode.asnQty')}
                    </th>
                    <th
                      style={{
                        width: '33.33%',
                        border: '1px solid #ddd',
                        textAlign: 'center',
                        background: '#ffffff'
                      }}
                    >
                      {formatMessageNew('languageObj.qrCode.systemQty')}
                    </th>
                  </tr>
                </thead>
              </table>
              <div style={{ maxHeight: '72px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    {scanResultList.map((item, idx) => (
                      <tr key={idx}>
                        <td style={{ width: '33.33%', border: '1px solid #ddd', textAlign: 'center' }}>
                          {renderSkuAndSize(item.skuAndSize)}
                        </td>
                        <td style={{ width: '33.33%', border: '1px solid #ddd', textAlign: 'center' }}>
                          {item.asnQty}
                        </td>
                        <td style={{ width: '33.33%', border: '1px solid #ddd', textAlign: 'center' }}>
                          {item.rfidScanQty}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr>
                    <td
                      style={{
                        width: '33.33%',
                        border: '1px solid #ddd',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        backgroundColor: '#f0f0f0'
                      }}
                    >
                      Total
                    </td>
                    <td
                      style={{
                        width: '33.33%',
                        border: '1px solid #ddd',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        backgroundColor: '#f0f0f0'
                      }}
                    >
                      {scanResultList && scanResultList.length > 0
                        ? scanResultList.reduce((sum, item) => sum + Number(item?.asnQty || 0), 0)
                        : 0}
                    </td>
                    <td
                      style={{
                        width: '33.33%',
                        border: '1px solid #ddd',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        backgroundColor: '#f0f0f0'
                      }}
                    >
                      {scanResultList && scanResultList.length > 0
                        ? scanResultList.reduce((sum, item) => sum + Number(item?.rfidScanQty || 0), 0)
                        : 0}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div
            style={{
              marginTop: 30,
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <h3 style={{ marginBottom: 8 }}>{formatMessageNew('languageObj.qrCode.comfirmScanResult')}</h3>
            <div
              className="scan_label2"
              style={{
                width: '95%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                marginLeft: '10px'
              }}
            >
              <div style={{ width: '95%' }}>
                {btns.map((key, idx) => (
                  <div className="displayFlex" key={key} style={{ justifyContent: 'center' }}>
                    <div
                      onClick={() => handleBtnClick(idx + 1)}
                      style={{
                        marginTop: 10,
                        background: activeIndex === idx + 1 ? '#6ca744' : '#fff',
                        color: activeIndex === idx + 1 ? '#fff' : '#6ca744',
                        border: '1px solid #6ca744',
                        cursor: 'pointer'
                      }}
                      className="phone-btn2 b1ddd alignC"
                    >
                      {formatMessageNew(key)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <Row style={{ marginTop: 10 }}>
            <Col className="mt15" style={{ textAlign: 'center' }} span={8}>
              <Button
                style={{ width: '90%' }}
                type="primary"
                className="phone-btn4 reset-btn mt0"
                onClick={backFirstPage}
              >
                {formatMessageNew('languageObj.qrCode.menu')}
              </Button>
            </Col>
            <Col className="mt15" style={{ textAlign: 'center' }} span={8}></Col>
            <Col className="mt15" style={{ textAlign: 'center' }} span={8}>
              <Button
                style={{ width: '90%' }}
                type="primary"
                className="phone-btn4 reset-btn mt0"
                onClick={closeCartonPage}
              >
                {formatMessageNew('languageObj.qrCode.nextCarton')}
              </Button>
            </Col>
          </Row>
        </div>
      )}
      {/* 页面三：环扫结果展示 */}
      {pageIndex === 2 && (
        <div
          style={{ height: 'auto', overflow: 'auto', marginLeft: -5, marginRight: -5 }}
          className="full-tag-wrapper qrcode-wrapper"
        >
          <div>
            <div style={{ marginBottom: 8, fontSize: 16, fontWeight: 700 }}>{formatMessageNew(titleKey)}</div>
            <div className="displayFlex alignCenter mt5">
              <div className="scan_label">{formatMessageNew('languageObj.qrCode.cartonNo')}：</div>
              <div className="scan_label2">
                <Input className="pt5 pb5" value={cartonNoValue} disabled={true} readOnly={inputReadOnly} />
              </div>
            </div>
          </div>
          {!scanRoundResultShowFlag && (
            <div style={{ marginTop: 30, background: 'lightgray', padding: 16, textAlign: 'center' }}>
              <span style={{ fontWeight: 600, color: '#898686', fontSize: 16 }}>
                {formatMessageNew('languageObj.qrCode.scanWarnMsg')}
              </span>
            </div>
          )}
          {scanRoundResultShowFlag && (
            <div style={{ marginTop: 30 }}>
              <h3 style={{ marginBottom: 8 }}>{formatMessageNew('languageObj.qrCode.scanResult')}</h3>
              <div style={{ marginBottom: 30 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th
                        style={{
                          width: '33.33%',
                          border: '1px solid #ddd',
                          textAlign: 'center',
                          background: '#ffffff'
                        }}
                      >
                        SKU
                      </th>
                      <th
                        style={{
                          width: '33.33%',
                          border: '1px solid #ddd',
                          textAlign: 'center',
                          background: '#ffffff'
                        }}
                      >
                        {formatMessageNew('languageObj.qrCode.asnQty')}
                      </th>
                      <th
                        style={{
                          width: '33.33%',
                          border: '1px solid #ddd',
                          textAlign: 'center',
                          background: '#ffffff'
                        }}
                      >
                        {formatMessageNew('languageObj.qrCode.systemQty')}
                      </th>
                    </tr>
                  </thead>
                </table>
                <div style={{ maxHeight: '72px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                      {roundScanResultList.map((item, idx) => (
                        <tr key={idx}>
                          <td style={{ width: '33.33%', border: '1px solid #ddd', textAlign: 'center' }}>
                            {renderSkuAndSize(item.skuAndSize)}
                          </td>
                          <td style={{ width: '33.33%', border: '1px solid #ddd', textAlign: 'center' }}>
                            {item.asnQty}
                          </td>
                          <td style={{ width: '33.33%', border: '1px solid #ddd', textAlign: 'center' }}>
                            {item.rfidScanQty}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    <tr>
                      <td
                        style={{
                          width: '33.33%',
                          border: '1px solid #ddd',
                          textAlign: 'center',
                          fontWeight: 'bold',
                          backgroundColor: '#f0f0f0'
                        }}
                      >
                        Total
                      </td>
                      <td
                        style={{
                          width: '33.33%',
                          border: '1px solid #ddd',
                          textAlign: 'center',
                          fontWeight: 'bold',
                          backgroundColor: '#f0f0f0'
                        }}
                      >
                        {roundScanResultList && roundScanResultList.length > 0
                          ? roundScanResultList.reduce((sum, item) => sum + Number(item?.asnQty || 0), 0)
                          : 0}
                      </td>
                      <td
                        style={{
                          width: '33.33%',
                          border: '1px solid #ddd',
                          textAlign: 'center',
                          fontWeight: 'bold',
                          backgroundColor: '#f0f0f0'
                        }}
                      >
                        {roundScanResultList && roundScanResultList.length > 0
                          ? roundScanResultList.reduce((sum, item) => sum + Number(item?.rfidScanQty || 0), 0)
                          : 0}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {scanRoundResultShowFlag && (
            <div className="displayFlex alignCenter mt8" style={{ marginTop: 30 }}>
              <div
                style={{
                  width: '130px',
                  fontSize: '13px'
                }}
              >
                {formatMessageNew('languageObj.qrCode.validationResult')}
              </div>
              <div className="scan_label2" style={{ width: '95%' }}>
                <div className="displayFlex">
                  {scanCompareResult && bingResultConfig[scanCompareResult] && (
                    <>
                      <div style={bingResultConfig[scanCompareResult].style} className="phone-btn2 b1ddd alignC">
                        {formatMessageNew(bingResultConfig[scanCompareResult].key)}
                      </div>
                      <Button
                        type="link"
                        icon={<ReloadOutlined />}
                        style={{ marginLeft: 8, marginTop: 4 }}
                        onClick={resetRoundCartonPage}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
          <Row style={{ marginTop: scanRoundResultShowFlag ? 10 : 210 }}>
            <Col className="mt15" style={{ textAlign: 'center' }} span={8}>
              <Button
                style={{ width: '90%' }}
                type="primary"
                className="phone-btn4 reset-btn mt0"
                onClick={backFirstPage}
              >
                {formatMessageNew('languageObj.qrCode.menu')}
              </Button>
            </Col>
            <Col className="mt15" style={{ textAlign: 'center' }} span={8}>
              <Button
                style={{ width: '90%' }}
                type="primary"
                className="phone-btn4 reset-btn mt0"
                onClick={() => toOpenCartonPage('roundScan')}
                disabled={!(cartonNoValue && cartonEnterFlag)}
              >
                {formatMessageNew('languageObj.qrCode.openCarton')}
              </Button>
            </Col>
            <Col className="mt15" style={{ textAlign: 'center' }} span={8}>
              <Button
                style={{ width: '90%' }}
                type="primary"
                className="phone-btn4 reset-btn mt0"
                onClick={closeCartonPage}
                disabled={!cartonNoValue}
              >
                {formatMessageNew('languageObj.qrCode.nextCarton')}
              </Button>
            </Col>
          </Row>
        </div>
      )}
      {/* 页面四：手动扫码页面 */}
      {pageIndex === 3 && (
        <div
          style={{ height: 'auto', overflow: 'auto', marginLeft: -5, marginRight: -5 }}
          className="full-tag-wrapper qrcode-wrapper"
        >
          <div>
            <Row>
              <Col style={{ textAlign: 'center' }} span={8}>
                <Button
                  style={{ width: '90%' }}
                  type="primary"
                  className="phone-btn4 reset-btn mt0"
                  onClick={() => submitManualScanFun(1)}
                  disabled={scanProductList.filter(e => e.ignoreFlag !== 1).length === 0}
                >
                  {formatMessageNew('languageObj.qrCode.submit')}
                </Button>
              </Col>
              <Col style={{ textAlign: 'center' }} span={8}></Col>
              <Col style={{ textAlign: 'center' }} span={8}>
                <Button
                  style={{ width: '90%' }}
                  type="primary"
                  className="phone-btn4 reset-btn mt0"
                  onClick={() => toOpenCartonPage('backScan')}
                  disabled={scanProductList.filter(e => e.ignoreFlag !== 1).length !== 0}
                >
                  {formatMessageNew('languageObj.qrCode.back')}
                </Button>
              </Col>
            </Row>
            <div className="displayFlex alignCenter" style={{ marginTop: 20 }}>
              <div className="scan_label">{formatMessageNew('languageObj.qrCode.cartonNo')}：</div>
              <div className="scan_label2">
                <Input className="pt5 pb5" value={cartonNoValue} disabled={true} readOnly={inputReadOnly} />
              </div>
            </div>
            <div className="displayFlex alignCenter mt5">
              <div className="scan_label">{formatMessageNew('languageObj.qrCode.rfidEpc')}：</div>
              <div style={{ fontSize: 13, flex: 1 }}>
                <Input
                  className="pt5 pb5"
                  ref={epcInputRef}
                  value={epcValue}
                  style={{ fontSize: 13 }}
                  readOnly={inputReadOnly}
                  allowClear
                  placeholder={`${formatMessageNew('languageObj.nsptagLocale.pleaseScan')}${formatMessageNew(
                    'languageObj.qrCode.rfidEpc'
                  )}`}
                  onFocus={onInputFocus}
                  onKeyDown={e => onInputKeyDown(e, 'epc')}
                  onChange={e => onInputChange(e, 'epc')}
                />
              </div>
            </div>
          </div>
          {manualScanStatus && (
            <div className="displayFlex alignCenter mt5" style={{ fontSize: 17, fontWeight: 'bold' }}>
              <div style={{ width: 85 }}>{formatMessageNew('languageObj.qrCode.sku')}：</div>
              <div>
                <span className="pt5 pb5">{skuValueByEpc}</span>
              </div>
            </div>
          )}
          {manualScanStatus && (
            <Col className="mt15" span={24} style={{ display: 'flex', justifyContent: 'center' }}>
              <div
                style={{
                  width: '96%',
                  height: 48,
                  fontSize: 25,
                  background: '#089820',
                  color: !manualStatus ? '#faf6f6' : '#efca04',
                  fontWeight: 500,
                  border: 'none',
                  textAlign: 'center',
                  borderRadius: 6 // 圆角
                }}
                type="primary"
              >
                {!manualStatus
                  ? formatMessageNew('languageObj.qrCode.manualAccurate')
                  : formatMessageNew('languageObj.qrCode.epcUpdate')}
              </div>
            </Col>
          )}
          {manualCountStatus && (
            <Col className="mt10" span={24} style={{ display: 'flex', justifyContent: 'center' }}>
              <div
                style={{
                  width: '96%',
                  fontSize: 15,
                  color: '#333',
                  fontWeight: 500,
                  textAlign: 'right',
                  marginTop: 5
                }}
              >
                {formatMessageNew('languageObj.qrCode.unitCount')}：{scanCount}/{totalCount}
              </div>
            </Col>
          )}
          <Row
            style={{
              marginTop: manualScanStatus ? (manualCountStatus ? 38 : 178) : manualCountStatus ? 138 : 178
            }}
          >
            <Col className="mt15" span={24} style={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                style={{
                  width: '96%',
                  height: 48,
                  fontSize: 18,
                  background: epcValue ? '#e6e3df' : '#efca04',
                  color: epcValue ? '#090909' : '#faf6f6',
                  border: 'none'
                }}
                type="primary"
                icon={<PrinterOutlined style={{ marginRight: 8 }} />} // 图标和文字间距
                onClick={() => setRelabelVisible(true)}
                disabled={epcValue}
              >
                {formatMessageNew('languageObj.qrCode.relabel')}
              </Button>
            </Col>
            {/*<Col className="mt15" style={{ textAlign: 'center' }} span={8}>*/}
            {/*  <Button*/}
            {/*    style={{ width: '90%' }}*/}
            {/*    type="primary"*/}
            {/*    className="phone-btn4 reset-btn mt0"*/}
            {/*    onClick={() => manualCloseCartonPage(3)}*/}
            {/*    disabled={scanProductList.length === 0}*/}
            {/*  >*/}
            {/*    {formatMessageNew('languageObj.qrCode.nextCarton')}*/}
            {/*  </Button>*/}
            {/*</Col>*/}
            <Col className="mt15" style={{ textAlign: 'center' }} span={8}>
              <Button
                style={{ width: '90%' }}
                type="primary"
                className="phone-btn4 reset-btn mt0"
                onClick={reScanPageData}
                disabled={reScanFlag}
              >
                {formatMessageNew('languageObj.qrCode.reScan')}
              </Button>
            </Col>
            <Col className="mt15" span={8}></Col>
            <Col className="mt15" style={{ textAlign: 'center' }} span={8}>
              <Button
                style={{ width: '90%' }}
                type="primary"
                className="phone-btn4 reset-btn mt0"
                onClick={reScanNextPiece}
                disabled={!epcValue}
              >
                {formatMessageNew('languageObj.qrCode.nextEpc')}
              </Button>
            </Col>
          </Row>
          <DialogMobile
            visible={scanUpcQrModalVisible}
            className="nike_mobile_modal"
            width="100%"
            content={
              <div>
                <div
                  className="displayFlex alignCenter"
                  style={{
                    marginBottom: 10,
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                    color: '#e74c3c'
                  }}
                >
                  <WarningOutlined
                    style={{
                      fontSize: 40,
                      color: '#e74c3c',
                      marginBottom: 6
                    }}
                  />
                  <span style={{ fontSize: 12, fontWeight: 600 }}>
                    {formatMessageNew('languageObj.qrCode.manualScanTip')}
                  </span>
                </div>
                {manualScanVisibleFlag && (
                  <div className="displayFlex alignCenter" style={{ marginBottom: 10 }}>
                    <Button
                      style={{
                        width: '100%',
                        height: 30,
                        fontSize: 18,
                        border: `1px solid #1677ff`,
                        color: '#fff',
                        background: '#3682ec',
                        fontWeight: 500,
                        borderRadius: 8
                      }}
                      disabled={true}
                    >
                      {formatMessageNew('languageObj.qrCode.physicalSkuCorrect')}
                    </Button>
                  </div>
                )}
                <div className="displayFlex alignCenter" style={{ marginBottom: 5 }}>
                  <div className="bind_model_label">{formatMessageNew('languageObj.qrCode.qrCode')}：</div>
                  <div className="bind_model_label2" style={{ flex: 1 }}>
                    <Input.TextArea
                      className="pt5 pb5"
                      value={qrCodeValue}
                      ref={qrCodeInputRef}
                      allowClear
                      readOnly={inputReadOnly}
                      placeholder={`${formatMessageNew('languageObj.nsptagLocale.pleaseScan')}${formatMessageNew(
                        'languageObj.qrCode.qrCode'
                      )}`}
                      onFocus={onInputFocus}
                      onKeyDown={e => onInputKeyDown(e, 'qr')}
                      onChange={e => onInputChange(e, 'qr')}
                      autoSize={{ minRows: 1, maxRows: 4 }} // 自动高度，最多4行
                    />
                  </div>
                </div>
                {skuValueByQr && (
                  <div
                    className="displayFlex alignCenter"
                    style={{ fontSize: 12, fontWeight: 500, color: '#40a9ff', marginBottom: 5 }}
                  >
                    <div style={{ width: '29%' }}></div>
                    <div>
                      <span className="bind_model_label2">{skuValueByQr}</span>
                    </div>
                  </div>
                )}
                <div className="displayFlex alignCenter" style={{ marginBottom: 5 }}>
                  <div className="bind_model_label">{formatMessageNew('languageObj.qrCode.upc')}：</div>
                  <div className="bind_model_label2" style={{ flex: 1 }}>
                    <Input
                      className="pt5 pb5"
                      value={upcValue}
                      ref={upcInputRef}
                      allowClear
                      readOnly={inputReadOnly}
                      onFocus={onInputFocus}
                      placeholder={`${formatMessageNew('languageObj.nsptagLocale.pleaseScan')}${formatMessageNew(
                        'languageObj.qrCode.upc'
                      )}`}
                      onKeyDown={e => onInputKeyDown(e, 'upc')}
                      onChange={e => onInputChange(e, 'upc')}
                    />
                  </div>
                </div>
                {skuValueByUpc && (
                  <div
                    className="displayFlex alignCenter"
                    style={{ fontSize: 12, fontWeight: 500, color: '#40a9ff', marginBottom: 5 }}
                  >
                    <div style={{ width: '29%' }}></div>
                    <div>
                      <span className="bind_model_label2">{skuValueByUpc}</span>
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
                  <Button
                    type="primary"
                    style={{ width: '48%' }}
                    className="phone-btn-dialog"
                    onClick={clearManualScanModal}
                  >
                    {formatMessageNew('languageObj.qrCode.cancel')}
                  </Button>
                  <Button
                    type="primary"
                    style={{ width: '48%' }}
                    className="phone-btn-dialog"
                    onClick={validateManualFun}
                    disabled={dialogConfirmVisible || (qrCodeValue && !qrCodeSkuFetched)}
                  >
                    {formatMessageNew('languageObj.qrCode.confirm')}
                  </Button>
                </div>
              </div>
            }
          />
          <DialogMobile
            visible={physicalSkuCorrectVisible}
            className="nike_mobile_modal"
            width="100%"
            content={
              <div>
                <div
                  className="displayFlex alignCenter"
                  style={{
                    marginBottom: 20,
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                    color: '#e74c3c'
                  }}
                >
                  <WarningOutlined
                    style={{
                      fontSize: 40,
                      color: '#e74c3c',
                      marginBottom: 6
                    }}
                  />
                  <span style={{ fontSize: 12, fontWeight: 600 }}>
                    {formatMessageNew('languageObj.qrCode.physicalSkuCorrectTip')}
                  </span>
                </div>
                <div className="displayFlex alignCenter" style={{ marginBottom: 15 }}>
                  <Button
                    style={{
                      width: '100%',
                      height: 35,
                      fontSize: 18,
                      border: `1px solid #1677ff`,
                      color: physicalSkuCorrectBtnActive ? '#fff' : '#3682ec',
                      background: physicalSkuCorrectBtnActive ? '#3682ec' : '#fff',
                      fontWeight: 500,
                      borderRadius: 8
                    }}
                    onClick={() => changeCorrectBtnStatus('correct')}
                  >
                    {formatMessageNew('languageObj.qrCode.physicalSkuCorrect')}
                  </Button>
                </div>
                <div className="displayFlex alignCenter" style={{ marginBottom: 5 }}>
                  <Button
                    style={{
                      width: '100%',
                      height: 35,
                      fontSize: 18,
                      border: `1px solid #1677ff`,
                      color: physicalSkuInCorrectBtnActive ? '#fff' : '#3682ec',
                      background: physicalSkuInCorrectBtnActive ? '#3682ec' : '#fff',
                      fontWeight: 500,
                      borderRadius: 8
                    }}
                    onClick={() => changeCorrectBtnStatus('inCorrect')}
                  >
                    {formatMessageNew('languageObj.qrCode.physicalSkuInCorrect')}
                  </Button>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
                  <Button
                    type="primary"
                    style={{ width: '48%' }}
                    className="phone-btn-dialog"
                    onClick={clearPhysicalCorrectModal}
                  >
                    {formatMessageNew('languageObj.qrCode.cancel')}
                  </Button>
                  <Button
                    type="primary"
                    style={{ width: '48%' }}
                    className="phone-btn-dialog"
                    onClick={toPhysicalCheckModal}
                    disabled={!physicalSkuCorrectBtnActive && !physicalSkuInCorrectBtnActive}
                  >
                    {formatMessageNew('languageObj.qrCode.confirm')}
                  </Button>
                </div>
              </div>
            }
          />
          <DialogMobile
            visible={epcBoundRelabelVisible}
            className="nike_mobile_modal"
            width="100%"
            content={
              <div>
                <div
                  className="displayFlex alignCenter"
                  style={{
                    marginBottom: 20,
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                    color: '#e74c3c'
                  }}
                >
                  <WarningOutlined
                    style={{
                      fontSize: 40,
                      color: '#e74c3c',
                      marginBottom: 6
                    }}
                  />
                  <span style={{ fontSize: 12, fontWeight: 600 }}>
                    {formatMessageNew('languageObj.qrCode.errorcode_7007')}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
                  <Button
                    type="primary"
                    style={{ width: '48%' }}
                    className="phone-btn-dialog"
                    onClick={cancelEpcBoundRelabelModal}
                  >
                    {formatMessageNew('languageObj.qrCode.cancel')}
                  </Button>
                  <Button
                    type="primary"
                    style={{ width: '48%' }}
                    className="phone-btn-dialog"
                    onClick={confirmEpcBoundRelabelModal}
                  >
                    {formatMessageNew('languageObj.qrCode.confirm')}
                  </Button>
                </div>
              </div>
            }
          />
          <DialogMobile
            visible={epcBoundSubmitVisible}
            className="nike_mobile_modal"
            width="100%"
            content={
              <div>
                <div
                  className="displayFlex alignCenter"
                  style={{
                    marginBottom: 20,
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                    color: '#e74c3c'
                  }}
                >
                  <WarningOutlined
                    style={{
                      fontSize: 40,
                      color: '#e74c3c',
                      marginBottom: 6
                    }}
                  />
                  <span style={{ fontSize: 12, fontWeight: 600 }}>
                    {formatMessageNew('languageObj.qrCode.confirmSubmitNextCarton')}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
                  <Button
                    type="primary"
                    style={{ width: '48%' }}
                    className="phone-btn-dialog"
                    onClick={cancelEpcBoundSubmitModal}
                  >
                    {formatMessageNew('languageObj.qrCode.cancel')}
                  </Button>
                  <Button
                    type="primary"
                    style={{ width: '48%' }}
                    className="phone-btn-dialog"
                    onClick={confirmEpcBoundSubmitModal}
                  >
                    {formatMessageNew('languageObj.qrCode.confirm')}
                  </Button>
                </div>
              </div>
            }
          />
          <DialogMobile
            visible={physicalSkuInCorrectVisible}
            className="nike_mobile_modal"
            width="100%"
            content={
              <div>
                <div
                  className="displayFlex alignCenter"
                  style={{
                    marginBottom: 20,
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                    color: '#e74c3c'
                  }}
                >
                  <WarningOutlined
                    style={{
                      fontSize: 40,
                      color: '#e74c3c',
                      marginBottom: 6
                    }}
                  />
                  <span
                    style={{ fontSize: 12, fontWeight: 600 }}
                    dangerouslySetInnerHTML={{ __html: formatMessageNew('languageObj.qrCode.manualInCorrectTip') }}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
                  <Button
                    type="primary"
                    style={{ width: '48%' }}
                    className="phone-btn-dialog"
                    onClick={clearPhysicalInCorrectModal}
                  >
                    {formatMessageNew('languageObj.qrCode.cancel')}
                  </Button>
                  <Button
                    type="primary"
                    style={{ width: '48%' }}
                    className="phone-btn-dialog"
                    onClick={() => submitManualScanFun(4)}
                  >
                    {formatMessageNew('languageObj.qrCode.confirm')}
                  </Button>
                </div>
              </div>
            }
          />
          <DialogMobile
            visible={relabelVisible}
            className="nike_mobile_modal"
            width="100%"
            content={
              <div>
                <div className="alignCenter" style={{ marginBottom: 15, textAlign: 'center' }}>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>
                    {formatMessageNew('languageObj.qrCode.labelTips')}
                  </span>
                </div>
                <div className="displayFlex alignCenter" style={{ marginBottom: 15 }}>
                  <Button
                    style={{
                      width: '100%',
                      height: 35,
                      fontSize: 18,
                      border: `1px solid #efca04`,
                      color: noRfidTagActive ? '#fff' : '#efca04',
                      background: noRfidTagActive ? '#efca04' : '#fff',
                      fontWeight: 500,
                      borderRadius: 8
                    }}
                    onClick={() => handleNoRfidTagClick('noRfidTag')}
                  >
                    {formatMessageNew('languageObj.qrCode.noRfidTag')}
                  </Button>
                </div>
                <div className="displayFlex alignCenter" style={{ marginBottom: 5 }}>
                  <Button
                    style={{
                      width: '100%',
                      height: 35,
                      fontSize: 18,
                      border: `1px solid #efca04`,
                      color: scanNoResponseActive ? '#fff' : '#efca04',
                      background: scanNoResponseActive ? '#efca04' : '#fff',
                      fontWeight: 500,
                      borderRadius: 8
                    }}
                    onClick={() => handleScanNoResponseClick('scanNoResponse')}
                  >
                    {formatMessageNew('languageObj.qrCode.scanNoResponse')}
                  </Button>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
                  <Button
                    type="primary"
                    style={{ width: '48%' }}
                    className="phone-btn-dialog"
                    onClick={() => {
                      setRelabelVisible(false)
                      setNoRfidTagActive(false)
                      setScanNoResponseActive(false)
                    }}
                  >
                    {formatMessageNew('languageObj.qrCode.cancel')}
                  </Button>
                  <Button
                    type="primary"
                    style={{ width: '48%' }}
                    className="phone-btn-dialog"
                    onClick={openReprintPage}
                    disabled={!noRfidTagActive && !scanNoResponseActive}
                  >
                    {formatMessageNew('languageObj.qrCode.confirm')}
                  </Button>
                </div>
              </div>
            }
          />
          <DialogMobile
            visible={showReprint}
            className="nike_mobile_modal"
            closeable={true} // 显示关闭按钮
            clear={closeReprintPage} // 关闭时调用
            width="100%"
            content={
              <div
                style={{
                  transform: 'scale(0.8)',
                  transformOrigin: 'top center',
                  width: '100%',
                  height: '100%'
                }}
              >
                <Button
                  type="primary"
                  style={{
                    position: 'absolute',
                    top: 10,
                    right: 197,
                    zIndex: 0,
                    fontSize: 16
                  }}
                  onClick={closeReprintPage}
                >
                  {formatMessageNew('languageObj.qrCode.backPage')}
                </Button>
                <ReprintPage
                  key={reprintKey}
                  reprintType="Honeywell"
                  relabelReason={relabelValue}
                  cartonNo={cartonNoValue}
                  reworkFlag={1}
                  // 将当前手动扫码时间戳传给 ReprintPage，保证和提交时一致
                  relabelReworkTime={
                    manualScanTimestampRef.current ||
                    (typeof window !== 'undefined' ? window.relabelReworkTime : Date.now())
                  }
                />
              </div>
            }
          />
          <DialogMobile
            visible={manualScanResultInvalidVisible}
            className="nike_mobile_modal"
            width="100%"
            content={
              <div>
                <div
                  className="displayFlex alignCenter"
                  style={{
                    marginBottom: 20,
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                    color: '#e74c3c'
                  }}
                >
                  <WarningOutlined
                    style={{
                      fontSize: 40,
                      color: '#e74c3c',
                      marginBottom: 6
                    }}
                  />
                  <span style={{ fontSize: 12, fontWeight: 600 }}>
                    {formatMessageNew('languageObj.qrCode.manualScanResultInvalidTipFon')}
                    <br />
                    {formatMessageNew('languageObj.qrCode.invalidScanResultTip')}
                    <br />
                    <div style={{ maxHeight: 120, overflowY: 'auto' }}>
                      {invalidList.map(e => (
                        <div key={e.skuAndSize}>
                          {renderSkuAndSize(e.skuAndSize)}-{Math.abs(e.invaildCount)}
                          {formatMessageNew('languageObj.qrCode.manualScanResultInvalidTipBac')}
                          {e.status === 'missing'
                            ? formatMessageNew('languageObj.qrCode.missing')
                            : formatMessageNew('languageObj.qrCode.exceeds')}
                        </div>
                      ))}
                    </div>
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
                  <Button
                    type="primary"
                    style={{ width: '48%' }}
                    className="phone-btn-dialog"
                    onClick={() => setManualScanResultInvalidVisible(false)}
                  >
                    {formatMessageNew('languageObj.qrCode.cancel')}
                  </Button>
                  <Button
                    type="primary"
                    style={{ width: '48%' }}
                    className="phone-btn-dialog"
                    onClick={() => submitManualScanFun(2)}
                  >
                    {formatMessageNew('languageObj.qrCode.confirm')}
                  </Button>
                </div>
              </div>
            }
          />
        </div>
      )}
    </ConfigProvider>
  )
}
export default RfIdScanningPage
